import { Link } from 'react-router';

import { GlassSurface } from '@components/glass/GlassSurface';
import { Icon } from '@components/ui/Icon';
import { SERVICE_ORBIT } from '@config/services';
import type { Service } from '@typings/content';

import styles from './ServiceOrbit.module.scss';

interface OrbitCoreProps {
  readonly service: Service;
  readonly panelId: string;
  readonly tabId: string;
}

/** How many technologies the core lists; the rest live on the services page. */
const STACK_PREVIEW = 3;

/**
 * The centre of the orbit: the selected service, described.
 *
 * Thick glass: the brightest thing on the page, the gradient orb, sits
 * directly behind it, and the text must stay legible over it. The orb
 * still shows as a glow around the rim, and bends there in liquid mode.
 *
 * A tab panel, so assistive technology hears it as the content of the
 * selected pill. The inner block is keyed by service, so each change
 * replays its entrance and the text visibly hands over rather than
 * swapping in place. The link is plain text, not glass: it sits inside
 * that fading block, and fading an ancestor of glass blanks the glass.
 */
export const OrbitCore = ({ service, panelId, tabId }: OrbitCoreProps) => (
  <GlassSurface
    variant="thick"
    className={styles.core}
    role="tabpanel"
    id={panelId}
    aria-labelledby={tabId}
    data-reveal=""
  >
    <div key={service.id} className={styles.coreContent}>
      <span className={styles.coreIcon}>
        <Icon name={service.icon} size={22} />
      </span>
      <p className={styles.coreTitle}>{service.title}</p>
      <p className={styles.coreSummary}>{service.summary}</p>
      <p className={styles.coreStack}>{service.stack.slice(0, STACK_PREVIEW).join(' · ')}</p>
      <Link to={`/services#${service.id}`} viewTransition className={styles.coreLink}>
        {SERVICE_ORBIT.explore}
        <span className="visually-hidden"> {service.title}</span>
        <Icon name="arrowRight" />
      </Link>
    </div>
  </GlassSurface>
);
